<template>
  <div v-if="loaded" class="flex flex-column">
    <div v-for="field in fields" :key="field.id" v-show="!field.condition || field.condition(formContext)" class="field" :class="{
      'flex-row-reverse justify-content-end align-items-center':
        field.horizontal != null && field.horizontal == true,
    }">
      <label v-if="field.title != null" :for="field.id" class="font-medium text-sm" :class="{
        'mb-0 ml-2': field.horizontal != null && field.horizontal == true,
      }">
        {{ field.title }}
        <i v-if="field.config?.tooltip" class="pi pi-info-circle ml-1 text-blue-500 cursor-pointer"
          v-tooltip="field.config.tooltip"></i>
        <small v-if="formContext?.phoneValid == false && field.title == 'Phone'" style="color: red; opacity: 0.5">Not
          valid</small>
      </label>

      <LocalizedTextField v-if="field.type == 'localized-text'" :id="field.id" :title="field.title" :help="field.help"
        :disabled="field.config != null ? field.config.disabled : false" :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)" />

      <LocalizedHtmlEditor v-if="field.type == 'localized-html'" :class="field.inputClass"
        :showHeaders="field.config != null ? field.config.disabled : false" :id="field.id"
        :aria-describedby="field.id + '-help'" :disabled="field.config != null ? field.config.disabled : false"
        :blackTextColor="field.config != null ? field.config.blackTextColor : false
          " :readonly="field.config != null ? field.config.readonly : false" :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)" />

      <MultiSelect v-if="field.type == 'multi' && field.config.format == null" :id="field.id"
        :aria-describedby="field.id + '-help'" :filter="true" :options="field.config.options"
        :optionLabel="field.config.optionLabel" :optionValue="field.config.optionValue"
        :placeholder="field.config.placeholder" :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)">
      </MultiSelect>

      <MultiSelect v-else-if="field.type == 'multi' && field.config.format != null" :id="field.id"
        :aria-describedby="field.id + '-help'" :options="field.config.options" :optionLabel="field.config.optionLabel"
        :optionValue="field.config.optionValue" :placeholder="field.config.placeholder" :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)">
        <template #value="slotProps">
          <div v-if="slotProps.value">
            {{ field.config.format(slotProps.value) }}
          </div>
          <span v-else>
            {{ slotProps.placeholder }}
          </span>
        </template>
        <template #option="slotProps">
          {{ field.config.format(slotProps.option) }}
        </template>
      </MultiSelect>

      <InputText v-if="field.type == 'text-input'" :id="field.id" :aria-describedby="field.id + '-help'"
        :disabled="field.config != null ? field.config.disabled : false"
        :readonly="field.config != null ? field.config.readonly : false" :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)" />

      <InputText v-if="field.type == 'text-datetime'" :id="field.id" :aria-describedby="field.id + '-help'"
        :disabled="field.config != null ? field.config.disabled : false" :readonly="true"
        :modelValue="formatDate(getProperty(field))" @update:modelValue="updateProperty(field, $event)" />

      <InputText v-else-if="field.type == 'country'" :id="field.id" :aria-describedby="field.id + '-help'"
        :disabled="field.config != null ? field.config.disabled : false" :readonly="true"
        :modelValue="getCountry(field)" />

      <InputText v-else-if="field.type == 'text-input-phone'" :id="field.id" :aria-describedby="field.id + '-help'"
        :disabled="field.config != null ? field.config.disabled : false"
        :readonly="field.config != null ? field.config.readonly : false" :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)" />

      <InputNumber v-else-if="field.type == 'number-input'" :locale="'da-DK'" :id="field.id"
        :aria-describedby="field.id + '-help'" :disabled="field.config != null ? field.config.disabled : false"
        :modelValue="getProperty(field)" :max="field.max" :min="field.min" :suffix="field.suffix"
        @update:modelValue="updateProperty(field, $event)" />

      <InputNumber v-else-if="field.type == 'number-input-decimal'" :locale="'da-DK'" :mode="'decimal'"
        :minFractionDigits="2" :id="field.id" :aria-describedby="field.id + '-help'"
        :disabled="field.config != null ? field.config.disabled : false" :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)" />

      <Textarea v-else-if="field.type == 'text-area'" :id="field.id" :aria-describedby="field.id + '-help'"
        :autoResize="true" rows="5" cols="30" :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)" />

      <InputSwitch v-else-if="field.type == 'switch'" :id="field.id" :aria-describedby="field.id + '-help'"
        :modelValue="getProperty(field)" @update:modelValue="updateProperty(field, $event)" />

      <Chips v-else-if="field.type == 'chips'" :id="field.id" :aria-describedby="field.id + '-help'"
        :modelValue="getProperty(field)" @update:modelValue="updateProperty(field, $event)" separator="," />

      <Calendar v-else-if="field.type == 'time'" :id="field.id" :showIcon="true" :aria-describedby="field.id + '-help'"
        :modelValue="getTimeProperty(field)" @update:modelValue="updateProperty(field, $event)" showTime
        hourFormat="24" />

      <Calendar v-else-if="field.type == 'time-only'" :id="field.id" :showIcon="true" :showClear="true"
        :showButtonBar="true" :manualInput="false" :aria-describedby="field.id + '-help'"
        :modelValue="getTimeProperty(field)" @update:modelValue="updateProperty(field, $event)" timeOnly
        hourFormat="24" />

      <Calendar
        v-else-if="field.type == 'calendar'"
        :id="field.id"
        :showIcon="field.config?.showIcon !== false"
        :showButtonBar="field.config?.showButtonBar || false"
        :aria-describedby="field.id + '-help'"
        :dateFormat="field.config?.dateFormat || 'dd-mm-yy'"
        :selectionMode="field.config?.selectionMode || 'single'"
        :placeholder="field.config?.placeholder"
        :minDate="field.config?.minDate"
        :maxDate="field.config?.maxDate"
        :modelValue="getCalendarProperty(field)"
        @update:modelValue="updateProperty(field, $event)"
      />

      <Dropdown v-else-if="field.type == 'dropdown' && field.config.format == null" :id="field.id"
        :aria-describedby="field.id + '-help'" :filter="field.filter" :showClear="field.filter"
        :options="field.config.options" :optionLabel="field.config.optionLabel" :optionValue="field.config.optionValue"
        :optionDisabled="field.config.optionDisabled"
        :placeholder="field.config.placeholder" :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)" />

      <Dropdown v-else-if="field.type == 'dropdown' && field.config.format != null" :id="field.id"
        :aria-describedby="field.id + '-help'" :options="field.config.options" :optionLabel="field.config.optionLabel"
        :optionValue="field.config.optionValue" :optionDisabled="field.config.optionDisabled"
        :placeholder="field.config.placeholder" :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)">
        <template #value="slotProps">
          <div v-if="slotProps.value">
            {{ field.config.format(slotProps.value) }}
          </div>
          <span v-else>
            {{ slotProps.placeholder }}
          </span>
        </template>
        <template #option="slotProps">
          {{ field.config.format(slotProps.option) }}
        </template>
      </Dropdown>

      <div v-else-if="field.type == 'imageUpload'" class="flex justify-content-center flex-wrap">
        <div class="w-full bg-gray-300 mb-1 flex justify-content-center flex-wrap">
          <!-- Video preview for video files -->
          <video 
            v-if="formContext[field.id] && isVideoFile(field)" 
            :src="getImageProperty(field)" 
            class="mx p-2 media-preview"
            controls 
            muted
            style="max-width: 250px; max-height: 250px;"
          />
          <!-- Image preview for image files -->
          <Image 
            v-else-if="formContext[field.id]" 
            class="mx p-2" 
            :src="getImageProperty(field)" 
            width="250" 
            preview 
          />
        </div>
        <div v-if="field.config != null ? !field.config.readonly : true" class="flex w-full gap-2">
          <Button v-if="formContext[field.id]" icon="pi pi-times" class="border-none bg-gray-500" aria-label="Cancel"
            @click="removeImage(field.id)" />
          <Button class="w-full h-14 bg-gray-200 border-none relative cursor-pointer flex" style="color: gray">
            <input style="opacity: 0" class="absolute w-full cursor-pointer"
              @change="updateImageProperty($event, field)" :id="field.id" :key="field.id" type="file" :accept="field.accept ? field.accept : 'image/png, image/jpeg, image/png'
                " />
            <p class="h-0 m-auto">
              {{
                formContext[field.id]
                  ? (isVideoFile(field) ? "Upload new video..." : "Upload new image...")
                  : (isVideoFile(field) ? "Upload video..." : "Upload image...")
              }}
            </p>
          </Button>
        </div>
      </div>

      <vue-tel-input v-else-if="field.type == 'phone-input'" :inputClasses="'bg-blue-400'"
        :modelValue="getProperty(field)" @update:modelValue="updateProperty(field, $event)"
        @validate="formContext.phoneValid = $event.valid"
        @country-changed="formContext.phoneCountryCode = $event.dialCode"
        :preferred-countries="['DK', 'NO', 'FI', 'GB']" :validCharactersOnly="true" />

      <html-editor v-else-if="field.type == 'html-input'" :class="field.inputClass"
        :showHeaders="field.config != null ? field.config.disabled : false" :id="field.id"
        :aria-describedby="field.id + '-help'" :disabled="field.config != null ? field.config.disabled : false"
        :blackTextColor="field.config != null ? field.config.blackTextColor : false
          " :readonly="field.config != null ? field.config.readonly : false" :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)" />

      <PerkConfigFormField v-else-if="field.type === 'perkConfig'" :perkConfigs="getProperty(field)"
        :help="field?.config?.help" :title="field?.config?.title" @update:perkConfigs="updateProperty(field, $event)" />

      <GoogleMapsAddress v-else-if="field.type === 'GoogleMapsAddress'" :context="getProperty(field)"
        @onChange="updateProperty(field, $event)" />

      <div v-else-if="field.type === 'range-slider'" class="range-slider-container">
        <Slider 
          :id="field.id" 
          :aria-describedby="field.id + '-help'"
          :modelValue="getProperty(field)"
          @update:modelValue="updateProperty(field, $event)"
          :range="field.config?.range || false"
          :min="field.config?.min || 0"
          :max="field.config?.max || 100"
          :step="field.config?.step || 1"
          :disabled="field.config?.disabled || false"
        />
        <div v-if="field.config?.range" class="range-values mt-2 text-sm text-gray-600">
          <span>{{ getProperty(field)?.[0] || field.config?.min || 0 }} - {{ getProperty(field)?.[1] || field.config?.max || 100 }}</span>
        </div>
        <div v-else class="range-value mt-2 text-sm text-gray-600">
          <span>{{ getProperty(field) || field.config?.min || 0 }}</span>
        </div>
      </div>

      <AlphaOnlyInput 
        v-else-if="field.type === 'AlphaOnlyInput'" 
        :field="field"
        :modelValue="getProperty(field)"
        @update:modelValue="updateProperty(field, $event)"
      />

      <DefaultExpirationDisplay
        v-else-if="field.type === 'DefaultExpirationDisplay'"
        :field="field"
        :modelValue="getProperty(field)"
        :context="formContext"
      />

      <component v-else :is="field.type" v-bind="{ context: formContext, field: field }" />

      <small v-if="field.help != null" :id="field.id + '-help'" class="mt-1">{{
        field.help
      }}</small>
    </div>

    <Button v-if="hasAction == null || hasAction == true" class="mt-3 form-action-button" @click="executeAction"
      :label="actionLabel" :icon="actionIcon" />
  </div>
</template>

<script>
import { ref, onMounted, watch, inject } from "vue";
import getCountries from "@/utils/getCountries.js";
import HtmlEditor from "./HtmlEditor.vue";
import PerkConfigFormField from "./PerkConfigFormField.vue";
import LocalizedTextField from "./LocalizedTextField.vue";
import LocalizedHtmlEditor from "./LocalizedHtmlEditor.vue";
import GoogleMapsAddress from "./GoogleMapsAddress.vue";
import FreeDaysDisplay from "./FreeDaysDisplay.vue";
import AlphaOnlyInput from "./AlphaOnlyInput.vue";
import DefaultExpirationDisplay from "./DefaultExpirationDisplay.vue";

function getByString(o, path) {
  var schema = o; // a moving reference to internal objects within obj
  var pList = path.split(".");

  var len = pList.length;
  for (var i = 0; i < len - 1; i++) {
    var elem = pList[i];
    if (!schema[elem]) schema[elem] = {};
    schema = schema[elem];
  }

  return schema[pList[len - 1]];
}

function setByString(o, path, value) {
  var schema = o; // a moving reference to internal objects within obj
  var pList = path.split(".");
  var len = pList.length;
  for (var i = 0; i < len - 1; i++) {
    var elem = pList[i];
    if (!schema[elem]) schema[elem] = {};
    schema = schema[elem];
  }

  schema[pList[len - 1]] = value;
}

export default {
  components: {
    HtmlEditor,
    PerkConfigFormField,
    LocalizedTextField,
    LocalizedHtmlEditor,
    GoogleMapsAddress,
    FreeDaysDisplay,
    AlphaOnlyInput,
    DefaultExpirationDisplay,
  },
  emits: ["onAction", "onChange"],
  props: ["context", "fields", "actionLabel", "actionIcon", "hasAction"],
  setup(props, { emit }) {
    watch(
      () => JSON.stringify(props.context),
      (updatedContext) => {
        updateFormContext(JSON.parse(updatedContext));
      }
    );

    const loaded = ref(false);
    const formContext = ref();
    const image = ref();

    const useFormatDate = inject("useFormatDate");

    function updateFormContext(context) {
      if (context != null)
        formContext.value = JSON.parse(JSON.stringify(context));
    }

    function getProperty(field) {
      if (field.path == null) {
        return formContext.value[field.id];
      }

      return getByString(formContext.value, field.path);
    }

    function getCalendarProperty(field) {
      const value = getProperty(field);
      
      // Handle null/undefined values - return null for single selection, empty array for multiple
      if (!value) {
        return (field.config?.selectionMode === 'multiple') ? [] : null;
      }
      
      // Handle arrays (for multiple selection mode)
      if (Array.isArray(value)) {
        return value.map(item => {
          if (item instanceof Date) {
            return item;
          }
          if (typeof item === 'string') {
            return new Date(item);
          }
          return item;
        });
      }
      
      // Handle single values
      if (value instanceof Date) {
        return value;
      }
      
      // If it's a string (like ISO date), convert to Date for calendar
      if (typeof value === 'string') {
        return new Date(value);
      }
      
      return value;
    }

    function getCountry(field) {
      if (field.path == null) {
        return getCountries().getCountryName(formContext.value[field.id]);
      }

      return getCountries().getCountryName(
        getByString(formContext.value, field.path)
      );
    }

    function getTimeProperty(field) {
      let value, date;
      
      if (field.path == null) {
        value = formContext.value[field.id];
        if (value == null) {
          // For time-only fields, return null to show empty field
          if (field.type === 'time-only') {
            return null;
          }
          date = new Date(Date.now());
          date.setHours(0, 0, 0, 0);
          formContext.value[field.id] = date;
          return date;
        }
        // Make sure we have a valid date object
        if (value instanceof Date) {
          return value;
        }
        return new Date(value);
      }

      value = getByString(formContext.value, field.path);
      if (value == null) {
        // For time-only fields, return null to show empty field
        if (field.type === 'time-only') {
          return null;
        }
        date = new Date(Date.now());
        date.setHours(0, 0, 0, 0);
        setByString(formContext.value, field.path, date);
        return date;
      }
      // Make sure we have a valid date object
      if (value instanceof Date) {
        return value;
      }
      return new Date(value);
    }

    function getImageProperty(field) {
      var file = formContext.value[field.id];
      if (!file || !file.name) {
        return file;
      }
      return URL.createObjectURL(file);
    }

    function removeImage(id) {
      formContext.value[id] = "";
      emit("onChange", formContext.value);
    }

    function updateProperty(field, payload) {
      let processedPayload = payload;
      
      // Ensure range-slider values are integers
      if (field.type === 'range-slider') {
        if (Array.isArray(payload)) {
          // For range sliders (two values)
          processedPayload = payload.map(value => Math.round(Number(value)));
        } else {
          // For single value sliders
          processedPayload = Math.round(Number(payload));
        }
      }
      
      if (field.path == null) {
        formContext.value[field.id] = processedPayload;
      } else {
        setByString(formContext.value, field.path, processedPayload);
      }

      emit("onChange", formContext.value);
    }

    function executeAction() {
      emit("onAction", formContext.value);
    }

    onMounted(() => {
      updateFormContext(props.context);

      loaded.value = true;
    });

    function updateImageProperty(event, field) {
      event.preventDefault();
      const input = document.getElementById(field.id);
      const file = input.files[0];

      formContext.value[field.id] = file;
      emit("onChange", formContext.value);
    }

    function isVideoFile(field) {
      const file = formContext.value[field.id];
      
      // Check if the accept attribute contains video mime types
      const acceptsVideo = field.accept && field.accept.includes('video/');
      
      // Check if the file itself is a video
      if (file && file.type && file.type.startsWith('video/')) {
        return true;
      }
      
      // Check if it's a string URL with video extensions (handle query params)
      if (typeof file === 'string') {
        const urlWithoutQuery = file.split('?')[0];
        if (urlWithoutQuery.endsWith('.mp4') || urlWithoutQuery.endsWith('.webm') || urlWithoutQuery.endsWith('.ogg')) {
          return true;
        }
        // For blob URLs, rely on the accept attribute to determine if it's a video
        if (file.startsWith('blob:') && acceptsVideo) {
          return true;
        }
      }
      
      // Default to accept attribute
      return acceptsVideo;
    }

    return {
      getProperty,
      getTimeProperty,
      getCountry,
      getImageProperty,
      updateProperty,

      formContext,
      updateImageProperty,
      removeImage,
      image,
      loaded,
      executeAction,

      formatDate: useFormatDate().formatDateTimeFull,
      getCalendarProperty,
      isVideoFile,
    };
  },
};
</script>

<style>
.vti__input {
  font-size: 1rem;
  color: #495057;
  padding: 0.5rem 0.5rem;
}
.vue-tel-input {
  border-color: lightgray;
}
.p-image-preview {
  transform: rotate(0) scale(0.6) !important;
}

.htmleditor .editor {
  background-image: linear-gradient(
    to right top,
    #081654,
    #081654,
    #122886,
    #122886,
    #122886
  );
}

/* Range slider styling */
.range-slider-container {
  padding: 0.5rem 0;
}

.range-slider-container .p-slider {
  background: #e9ecef;
  border-radius: 6px;
  height: 6px;
}

.range-slider-container .p-slider .p-slider-range {
  background: linear-gradient(135deg, #3b82f6 0%, #9333ea 100%);
  border-radius: 6px;
}

.range-slider-container .p-slider .p-slider-handle {
  background: white;
  border: 2px solid #3b82f6;
  width: 20px;
  height: 20px;
  margin-top: -7px;
  border-radius: 50%;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.range-slider-container .p-slider .p-slider-handle:hover {
  border-color: #2563eb;
  transform: scale(1.1);
  box-shadow: 0 4px 8px rgba(59, 130, 246, 0.3);
}

.range-values, .range-value {
  text-align: center;
  font-weight: 500;
  color: #64748b;
}

.white .editor {
  background: white;
}

/* Video preview styling */
.media-preview {
  border-radius: 0.5rem;
  background: #000;
}
</style>
